import PropTypes from 'prop-types';
import React, { useRef, useState, useMemo } from 'react';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';
import { useKeyPressEvent } from 'react-use';
import { makeStyles } from 'tss-react/mui';

import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import QPopper from 'components/QPopper';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

const useStyles = makeStyles()((theme) => ({
  dumpButton: {
    position: 'absolute', // 'fixed' doesn't work with scroll
    zIndex: theme.zIndex.mobileStepper,
    color: 'black',
    backgroundColor: 'white',
  },
  content: {
    maxWidth: '60vw',
    maxHeight: '60vh',
    overflow: 'scroll',
  },
}));

const stopProp = (e) => e?.stopPropagation();

const Dump = (props) => {
  const { id, children, obj, value, style, ...otherProps } = props;
  const { classes } = useStyles();
  const buttonRef = useRef();
  const contentRef = useRef();
  const [show, setShow] = useState();
  const [showDetails, setShowDetails] = useState();
  const [pinWindow, setPinWindow] = useState();
  const [dragDataX, setDragDataX] = useState(0);
  const [dragDataY, setDragDataY] = useState(0);
  const offset = useMemo(() => [dragDataX, dragDataY], [dragDataY, dragDataX]);
  useKeyPressEvent(
    'Alt',
    () => setShow(true),
    () => setShow(false),
  );

  const val = obj ?? value;
  const data = useMemo(() => (showDetails && typeof val === 'function' ? val() : val), [val, showDetails]);

  return (
    <>
      {(show || showDetails) && (
        <Button
          startIcon={<BuildOutlinedIcon fontSize='small' />}
          variant='outlined'
          className={classes.dumpButton}
          {...otherProps}
          onClick={(event) => {
            buttonRef.current = event.currentTarget;
            setShowDetails(true);
            setDragDataX(0);
            setDragDataY(0);
            event.stopPropagation();
          }}
          size='small'
          component='div' // div to prevent 'nested button' html complaint
          role='button'
        >
          <Typography variant='captionRegular' noWrap>
            {id ?? data?.id}
          </Typography>

          {(id ?? data?.id) && (
            <IconButton
              onClick={(event) => {
                navigator.clipboard.writeText(id ?? data?.id);
                event.stopPropagation();
              }}
              size='small'
            >
              <ContentCopyIcon fontSize='inherit' />
            </IconButton>
          )}
        </Button>
      )}

      {(showDetails || pinWindow) && (
        <Draggable
          handle='#header'
          onDrag={(event, uiData) => {
            const newDragDataX = dragDataX + (uiData.deltaX || 0);
            setDragDataX(newDragDataX);
            const newDragDataY = dragDataY + (uiData.deltaY || 0);
            setDragDataY(newDragDataY);
            if (!pinWindow) {
              setPinWindow(true);
            }
          }}
        >
          <QPopper
            anchorEl={buttonRef.current}
            open={showDetails}
            placement='bottom-start'
            onClickAway={() => !pinWindow && setShowDetails(false)}
            modifiers={[
              {
                name: 'offset',
                options: { offset },
              },
              {
                name: 'flip',
                enabled: false,
              },
              {
                name: 'preventOverflow',
                enabled: false,
              },
            ]}
            onClick={stopProp}
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              id='header'
              backgroundColor='border.light'
              sx={{ cursor: 'move' }}
            >
              <DragIndicatorIcon />
              <Button
                variant='outlined'
                onClick={(event) => {
                  navigator.clipboard.writeText(contentRef.current.innerText);
                  event.stopPropagation();
                  dispatchSimpleNotification('Copied to Clipboard');
                }}
              >
                copy to clipboard
              </Button>

              <IconButton
                onClick={(event) => {
                  setPinWindow(!pinWindow);
                  event.stopPropagation();
                }}
                size='large'
              >
                {pinWindow ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
              </IconButton>

              <IconButton
                onClick={(event) => {
                  setShowDetails(false);
                  event.stopPropagation();
                }}
                size='large'
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <div ref={contentRef} className={classes.content}>
              {children || null}
              {data && (
                <pre>
                  <Typography variant='paragraphRegular'>{JSON.stringify(data, null, 2)}</Typography>
                </pre>
              )}
            </div>
          </QPopper>
        </Draggable>
      )}
    </>
  );
};

Dump.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  obj: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]),
  id: PropTypes.any,
  style: PropTypes.object,
};

function DumpWrapper(props) {
  const debug = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'debug'));
  if (!debug) {
    return null;
  }
  return <Dump {...props} />;
}

DumpWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  obj: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]),
  id: PropTypes.any,
  style: PropTypes.object,
};

export default DumpWrapper;
